"use client";

import { Tooltip } from "@dc/ui/components";
import { useTranslations } from "next-intl";
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { DangerBold } from "solar-icon-set";

export const SUPPORT_EMAIL = "support@documentcrunch.com";
const SUPPORT_EMAIL_URL = `mailto:${SUPPORT_EMAIL}`;

export const TrialSetupReportedNotice = ({ supportNotified }: { supportNotified: boolean }) => {
  const t = useTranslations("trials.register_public");
  const [copied, setCopied] = useState(false);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  const handleCopy = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (!navigator.clipboard?.writeText) return;

    event.preventDefault();
    const mailtoUrl = event.currentTarget.href;

    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);

      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
      setCopied(true);
      copyFeedbackTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      window.location.assign(mailtoUrl);
    }
  };

  return (
    <div aria-live="polite" className="flex flex-col items-center gap-4 text-center" role="alert">
      <DangerBold aria-hidden className="size-16 text-bg-indicator-danger!" />
      <h1 className="font-semibold text-2xl leading-tight md:text-3xl">{t("failed.title")}</h1>
      <p className="max-w-sm text-muted-foreground text-sm">
        {t.rich(supportNotified ? "support.body_notified" : "support.body_contact", {
          email: SUPPORT_EMAIL,
          support: (chunks: ReactNode) => (
            <Tooltip
              color="foreground"
              content={t("support.copied")}
              disableAnimation
              isOpen={copied}
              placement="bottom"
            >
              <a className="text-primary underline" href={SUPPORT_EMAIL_URL} onClick={handleCopy}>
                {chunks}
              </a>
            </Tooltip>
          ),
        })}
      </p>
    </div>
  );
};
